import { useEffect, useState, type FC, type PropsWithChildren } from 'react';
import {
  asyncWithLDProvider,
  type ProviderConfig,
} from 'launchdarkly-react-client-sdk';

/**
 * LaunchDarkly provider for the Loyalty Console SPA.
 *
 * Initializes the React Web SDK before children render so that flag values
 * are available on first paint. The client-side ID is read from
 * `VITE_LD_CLIENT_SIDE_ID` (set via .env.local in dev, CI env in prod).
 *
 * The user context starts anonymous; once we know who is logged in we can
 * call `useLDClient().identify(...)` to switch to a real user context
 * without remounting the provider.
 */
export default function LDProvider({ children }: PropsWithChildren) {
  const [wrapper, setWrapper] = useState<{
    Component: FC<PropsWithChildren>;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;

    const clientSideID = import.meta.env.VITE_LD_CLIENT_SIDE_ID;

    if (!clientSideID) {
      // No key configured: render children unwrapped so the app still works.
      // Flag evaluations will return their fallback values.
      // eslint-disable-next-line no-console
      console.warn(
        '[LaunchDarkly] VITE_LD_CLIENT_SIDE_ID is not set; flags will use fallback values.'
      );
      setWrapper({ Component: PassThrough });
      return;
    }

    const config: ProviderConfig = {
      clientSideID,
      context: {
        kind: 'user',
        anonymous: true,
        key: 'anonymous-bootstrap',
      },
      reactOptions: {
        useCamelCaseFlagKeys: false,
      },
      options: {
        // Send fewer events while we are bootstrapping; tune later as needed.
        sendEvents: true,
      },
    };

    asyncWithLDProvider(config)
      .then((Provider) => {
        if (!cancelled) {
          setWrapper({ Component: Provider as FC<PropsWithChildren> });
        }
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('[LaunchDarkly] Failed to initialize SDK:', err);
        if (!cancelled) {
          setWrapper({ Component: PassThrough });
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (!wrapper) return null;
  const { Component } = wrapper;
  return <Component>{children}</Component>;
}

const PassThrough: FC<PropsWithChildren> = ({ children }) => <>{children}</>;
